// XSM-1536 - SSO Feature
// Author: Abhishek Bharti
// Updated on 20/11/2025
import { useEffect, useRef, useCallback } from "react";
import { XSM_BASE_URL } from "../constants/auth";
import { getSSOErrorMessage } from "../utils/ssoErrorMessages";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface SSOAuthConfig {
  clientId: string;
  redirectURL: string;
  xsmDomain: string;
}

interface SSOAuthState {
  sessionDataKey: string | null;
  code: string | null;
  error: string | null;
}

interface UseSSOAuthorizationOptions {
  config: SSOAuthConfig;
  authState: SSOAuthState;
}

const LAST_AUTH_ATTEMPT_KEY = "dm_last_auth_attempt";
const STALE_THRESHOLD = 10 * 60 * 1000; // 10 minutes
const AUTHORIZATION_URL = `${XSM_BASE_URL}/xsm-api-sso/v2/proxy/oauth2/authorize`;

/**
 * Custom hook to manage SSO authorization flow
 *
 * Features:
 * - Initial authorization on mount (if no sessionDataKey)
 * - Periodic authorization checks every 10 minutes
 * - Cooldown mechanism to prevent rapid successive authorization attempts
 * - Automatic cleanup when code is received
 *
 * @param options - Configuration and state for SSO authorization
 */
export const useSSOAuthorization = ({
  config,
  authState,
}: UseSSOAuthorizationOptions) => {
  const { clientId, redirectURL, xsmDomain } = config;
  const { sessionDataKey, code, error } = authState;

  // Refs to track state without causing re-renders
  const intervalRef = useRef<number | null>(null);
  const initialAuthorizationTriggeredRef = useRef(false);
  const hasProcessedCodeRef = useRef(false);
  const lastDisplayedErrorRef = useRef<string | null>(null);

  /**
   * Check if enough time has passed since the last authorization attempt
   */
  const canRetryAuthorization = useCallback(() => {
    try {
      const lastAttempt = Number(
        window.localStorage.getItem(LAST_AUTH_ATTEMPT_KEY) || "0"
      );
      const timeSinceLastAttempt = Date.now() - lastAttempt;
      return timeSinceLastAttempt > STALE_THRESHOLD;
    } catch (e) {
      console.warn("Error checking retry authorization:", e);
      return true; // Allow attempt if localStorage is unavailable
    }
  }, []);

  /**
   * Perform the OAuth2 authorization redirect
   */
  const performAuthorization = useCallback(() => {
    try {
      // Record the authorization attempt timestamp
      window.localStorage.setItem(LAST_AUTH_ATTEMPT_KEY, String(Date.now()));

      // Build authorization URL
      const url = new URL(AUTHORIZATION_URL);
      url.searchParams.set("response_type", "code");
      url.searchParams.set("client_id", clientId);
      url.searchParams.set("redirect_uri", redirectURL);
      url.searchParams.set("XSM-Domain", xsmDomain);
      url.searchParams.set("Xsm-application", "eves");
      url.searchParams.set("scope", "openid");

      console.log("Redirecting to authorization URL");
      window.location.assign(url.toString());
    } catch (error) {
      console.error("Authorization error:", error);
      toast.error("Authorization failed. Please try again.");
    }
  }, [clientId, redirectURL, xsmDomain]);

  /**
   * Trigger authorization with optional cooldown bypass
   */
  const authorize = useCallback(
    (bypassCooldown = false) => {
      // Don't authorize if code has already been processed
      if (hasProcessedCodeRef.current) {
        console.log("Authorization blocked - code already processed");
        return;
      }

      // Check cooldown unless explicitly bypassed
      if (!bypassCooldown && !canRetryAuthorization()) {
        console.log("Authorization blocked - cooldown period active");
        return;
      }

      console.log("Triggering authorization", {
        bypassCooldown,
        timestamp: new Date().toISOString(),
      });

      initialAuthorizationTriggeredRef.current = true;
      performAuthorization();
    },
    [canRetryAuthorization, performAuthorization]
  );

  /**
   * Clear the authorization check interval
   */
  const clearAuthorizationInterval = useCallback(() => {
    if (intervalRef.current) {
      console.log("Clearing authorization interval");
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Start the periodic authorization check interval
   */
  const startAuthorizationInterval = useCallback(() => {
    console.log("Starting authorization interval");

    // Clear any existing interval
    clearAuthorizationInterval();

    // Set up new interval
    intervalRef.current = window.setInterval(() => {
      console.log("Interval check triggered", {
        timestamp: new Date().toISOString(),
        initialTriggered: initialAuthorizationTriggeredRef.current,
        hasProcessedCode: hasProcessedCodeRef.current,
      });

      // Don't proceed if code has been processed
      if (hasProcessedCodeRef.current) {
        console.log("Skipping - code already processed");
        clearAuthorizationInterval();
        return;
      }

      // Check current state
      const currentParams = new URLSearchParams(window.location.search);
      const hasCode = Boolean(currentParams.get("code"));
      const hasAccessToken = Boolean(
        window.localStorage.getItem("dm_access_token")
      );

      // If code exists, clear interval as authorization is complete
      if (hasCode) {
        console.log("Code found - clearing interval");
        clearAuthorizationInterval();
        return;
      }

      // For idle state with sessionDataKey, refresh authorization every 10 minutes
      // This ensures the session stays valid even when user is idle
      console.log("Refreshing authorization (periodic check)", {
        hasCode,
        hasAccessToken,
        timestamp: new Date().toISOString(),
      });
      authorize();
    }, STALE_THRESHOLD);
  }, [authorize, clearAuthorizationInterval]);

  /**
   * Main effect - orchestrates the authorization flow
   */
  useEffect(() => {
    // Handle error display - only show once per unique error
    if (error && error !== lastDisplayedErrorRef.current) {
      console.error("SSO authorization error:", error);
      toast.error(getSSOErrorMessage(error));
      lastDisplayedErrorRef.current = error;
    } else if (!error) {
      // Reset the ref when error is cleared
      lastDisplayedErrorRef.current = null;
    }

    // Early return if code is present (successful authorization)
    if (code) {
      if (!hasProcessedCodeRef.current) {
        hasProcessedCodeRef.current = true;
        console.log("Authorization code received - cleaning up");
        window.localStorage.removeItem(LAST_AUTH_ATTEMPT_KEY);
        clearAuthorizationInterval();
      }
      return;
    }

    // Trigger initial authorization if no sessionDataKey
    if (!sessionDataKey && !initialAuthorizationTriggeredRef.current) {
      console.log("No sessionDataKey - triggering initial authorization");
      initialAuthorizationTriggeredRef.current = true;
      authorize(true); // Bypass cooldown for initial attempt
    } else if (sessionDataKey && !initialAuthorizationTriggeredRef.current) {
      // If sessionDataKey exists (idle state), mark as triggered so interval can work
      console.log("SessionDataKey exists - enabling periodic refresh");
      initialAuthorizationTriggeredRef.current = true;
    }

    // Start periodic authorization checks (runs every 10 minutes)
    // This will refresh authorization for both initial state and idle state with sessionDataKey
    startAuthorizationInterval();

    // Cleanup on unmount
    return () => {
      console.log("Cleaning up SSO authorization");
      clearAuthorizationInterval();
    };
  }, [
    code,
    sessionDataKey,
    error,
    authorize,
    startAuthorizationInterval,
    clearAuthorizationInterval,
  ]);

  return {
    isAuthorizationInProgress: !code,
    hasAuthorizationCode: Boolean(code),
    authorize,
    clearAuthorizationInterval,
  };
};
